import React from "react";
import { Loader2, Save, ShieldCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card } from "@/components/dash/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Profile() {
  const { organization, refresh } = useAuth();
  const [f, setF] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (organization) setF({
      name: organization.name || "", industry: organization.industry || "", location: organization.location || "",
      size: organization.size || "", website: organization.website || "", gst: organization.gst || "",
      pan: organization.pan || "", msme: organization.msme || "", description: organization.description || "",
      services: (organization.services || []).join(", "), technologies: (organization.technologies || []).join(", "),
      contact_phone: organization.contact_phone || "",
    });
  }, [organization]);

  if (!organization || !f) return <div className="text-slate-400">Loading profile…</div>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/organizations/${organization.id}`, {
        ...f, services: f.services.split(",").map((s) => s.trim()).filter(Boolean),
        technologies: f.technologies.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Profile updated"); await refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Company Profile" description="Manage your organization's business profile and KYC information." breadcrumb="Business / Profile">
        <Button onClick={save} disabled={saving} className="bg-vs-royal text-white" data-testid="profile-save"><Save className="h-4 w-4 mr-1.5" />{saving ? "Saving…" : "Save Changes"}</Button>
      </PageHeader>
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" title="Company Information">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Company Name"><Input value={f.name} onChange={set("name")} data-testid="profile-name" /></F>
            <F label="Industry"><Input value={f.industry} onChange={set("industry")} /></F>
            <F label="Location"><Input value={f.location} onChange={set("location")} /></F>
            <F label="Company Size"><Input value={f.size} onChange={set("size")} /></F>
            <F label="Website"><Input value={f.website} onChange={set("website")} /></F>
            <F label="Contact Phone"><Input value={f.contact_phone} onChange={set("contact_phone")} /></F>
            <F label="Services" full><Input value={f.services} onChange={set("services")} placeholder="Comma separated" /></F>
            <F label="Technologies" full><Input value={f.technologies} onChange={set("technologies")} placeholder="Comma separated" /></F>
            <F label="About" full><Textarea value={f.description} onChange={set("description")} rows={4} /></F>
          </div>
        </Card>
        <div className="space-y-6">
          <Card title="Verification">
            <div className="flex items-center gap-2 mb-4"><ShieldCheck className="h-5 w-5 text-emerald-600" /><StatusBadge status={organization.verification_status} /></div>
            <p className="text-sm text-slate-500">Your KYC and compliance status on VS PartnerHub.</p>
          </Card>
          <Card title="KYC Details">
            <div className="space-y-4">
              <F label="GST"><Input value={f.gst} onChange={set("gst")} /></F>
              <F label="PAN"><Input value={f.pan} onChange={set("pan")} /></F>
              <F label="MSME"><Input value={f.msme} onChange={set("msme")} /></F>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
const F = ({ label, children, full }) => (<div className={full ? "sm:col-span-2" : ""}><Label>{label}</Label><div className="mt-1.5">{children}</div></div>);
